import { useEffect } from 'react';

import { useRouter } from 'next/router';

import { isNonProductionAnalyticsHost } from '@onark/shared/analyticsEnv';
import { surfaceForPath } from '@onark/shared/analyticsSurface';
import posthogJs from 'posthog-js';

// posthog-js's ES-module build has no pre-init command queue (unlike the
// script snippet): capture() before init() is silently dropped. React runs
// child effects before parent effects, so a page's mount-time capture fires
// BEFORE _app's init on every full page load — which is every ad-click
// arrival. welcomeAboardCheckoutStarted shipped 2026-07-10 and recorded zero
// events for exactly this reason. Buffer pre-init events here and flush them
// from init's `loaded` callback; components should capture through this
// helper, not posthogJs directly.
const PRE_INIT_QUEUE_MAX = 25;

// Events emitted while the page is going away. posthog-js's own pagehide
// handler flushes its batch queue BEFORE a later-registered listener (ours)
// runs, so a normally-captured event is queued after the flush, on a timer
// that never fires, and is lost. Options without `_batchKey` bypass the batch
// queue (posthog-core.js `capture`), so these go straight out by sendBeacon,
// which also survives a phone tab being backgrounded.
const BEACON_EVENTS = new Set(['signup_trial.card_step_abandoned']);
const preInitQueue: {
  event: string;
  properties?: Record<string, unknown>;
}[] = [];
let sdkLoaded = false;

export function capture(
  event: string,
  properties?: Record<string, unknown>
): void {
  if (typeof window === 'undefined') return;

  if (sdkLoaded) {
    if (BEACON_EVENTS.has(event)) {
      posthogJs.capture(event, properties, { transport: 'sendBeacon' });
    } else {
      posthogJs.capture(event, properties);
    }

    return;
  }

  if (preInitQueue.length < PRE_INIT_QUEUE_MAX) {
    preInitQueue.push({ event, properties });
  }
}

// One-shot SDK init, shared by BOTH router trees: legacy pages/_app.tsx (via
// usePostHog) and the App Router (via app/_components/AnalyticsProvider). The
// module-level guard is what makes that safe — posthog-js 1.372 resets session
// state on re-init (see usePostHog's comment), and with two provider trees in
// one bundle whichever mounts first must win. Before this existed, App Router
// pages (/start/*, /sign-up, /admin v2) never initialized the SDK at all, so
// every capture() there — including the whole signup_trial.* funnel catalog —
// queued forever and silently vanished.
let initStarted = false;

/**
 * Adopt an SDK that is already live: stamp the surface, open the capture
 * valve, and drain everything that emitted before this point.
 *
 * Shared by the `loaded` callback below and the already-initialized branch in
 * `initPostHogOnce` — the two paths that can hand us a usable SDK.
 */
function adoptLoadedSdk(): void {
  sdkLoaded = true;
  // Stamp the surface BEFORE anything is sent, so every event carries it.
  posthogJs.register({ surface: surfaceForPath(window.location.pathname) });
  preInitQueue
    .splice(0)
    .forEach(({ event, properties }) => posthogJs.capture(event, properties));
}

/** Has some other tree already booted posthog-js on this page load? */
const posthogAlreadyLoaded = (): boolean =>
  // `__loaded` is posthog-js's own "init finished" flag. Read through a cast:
  // it isn't in the public type surface, and we only ever test it.
  Boolean((posthogJs as unknown as { __loaded?: boolean }).__loaded);

/**
 * The prod PostHog key is COMMITTED in apps/frontend/.env (NEXT_PUBLIC_* is
 * build-time-inlined, see the comment there), so every build carries it —
 * including Railway PR previews and local dev. Gate by runtime hostname: real
 * visitors arrive on onark.app or a church's custom domain, never on a
 * *.railway.app host or localhost. Without this, preview QA wrote straight
 * into the prod project — `posthog-gate-check-church-b` entered the
 * first-guest-check-in cohort from `frontend-onark-pr-283.up.railway.app`
 * (2026-08-05). Override for deliberate local analytics debugging with
 * NEXT_PUBLIC_POSTHOG_CAPTURE_NONPROD=true in .env.local.
 */
function captureBlockedOnThisHost(): boolean {
  if (process.env.NEXT_PUBLIC_POSTHOG_CAPTURE_NONPROD === 'true') return false;

  return isNonProductionAnalyticsHost(window.location.hostname);
}

export function initPostHogOnce(): void {
  if (typeof window === 'undefined' || initStarted) return;

  initStarted = true;

  if (captureBlockedOnThisHost()) {
    console.info(
      `[posthog] capture disabled on non-production host ${window.location.hostname}`
    );

    return;
  }

  // The marketing app boots posthog-js through its OWN init
  // (marketing/components/analytics.tsx) with a different pageview config, and
  // both trees share one posthog-js module instance. Since the signup form
  // embeds on marketing landings, that tree is now routinely already live when
  // this runs — and a second init() RESETS session state (posthog-js ≥1.372),
  // which is precisely the bug the module guard above exists to prevent.
  // Adopt the running SDK instead: the queue still has to be flushed or every
  // signup_trial.* event emitted on a marketing landing vanishes silently.
  if (posthogAlreadyLoaded()) {
    adoptLoadedSdk();

    return;
  }

  // The key is inlined at BUILD time (apps/frontend/.env). If it's missing,
  // posthog-js never initializes and capture is dead for EVERY visitor — the
  // exact outage we shipped once when the build didn't inject it. This must
  // be LOUD, not a silent skip: report to Rollbar before bailing. The guard
  // above makes this ~one report per full page load, not per navigation.
  if (!process.env.NEXT_PUBLIC_POSTHOG_API_KEY) {
    window.Rollbar?.error(
      'PostHog disabled: NEXT_PUBLIC_POSTHOG_API_KEY missing from the build — analytics capture is OFF for all visitors'
    );

    return;
  }
  // person_profiles: 'always' creates profiles for anonymous ad-clickers too,
  // so $initial_utm_* persists and merges into the identified user on signup.

  //
  // api_host is a FIRST-PARTY path (/ingest) proxied to PostHog by the
  // app/ingest Route Handlers. Serving analytics from our own origin stops
  // content/ad blockers — which blocklist *.posthog.com (the same lists that block
  // fbevents.js and google-analytics, and that drop events for a large share
  // of real church-admin traffic) — from silently killing capture. Verified:
  // events POSTed through /ingest DO land in PostHog. (Ingestion can lag
  // 15-20m in the ClickHouse query API, so a proxied event missing after a
  // few seconds is lag, not loss — wait before concluding a drop.)
  // next.config sets skipTrailingSlashRedirect so posthog-js's trailing-slash
  // paths (/i/v0/e/) proxy through without a 308 stripping the slash.
  //
  // ui_host is the PostHog app UI (not the ingestion host) so the toolbar
  // and "view in PostHog" deep-links resolve correctly.
  let phLoaded = false;

  try {
    posthogJs.init(process.env.NEXT_PUBLIC_POSTHOG_API_KEY, {
      api_host: '/ingest',
      // PINNED, not a behavior change: `true` is what 1.398 already resolves
      // to for us, because we never pass `defaults`. The SDK's own resolution
      // reads `capture_pageview: !defaults || '2025-05-24' > defaults ||
      // 'history_change'`, so adding `defaults: '2025-05-24'` (or a future
      // posthog-js moving its baseline) silently flips this to
      // 'history_change'. Both values capture the pageview on full page load
      // — the initial-pageview branch only tests truthiness — but
      // 'history_change' ALSO patches pushState/replaceState, and every SPA
      // navigation would then be counted twice: once by the SDK, once by
      // trackSpaNavigation below. That second capture is also the one that
      // re-stamps `surface`, so it can't just be dropped in favour of the
      // SDK's. `true` = the SDK owns the arrival pageview, we own SPA
      // navigations. Locked by __tests__/posthog-pageview-capture.test.ts.
      // Client-side crashes, tied to the person and session that hit them.
      //
      // Until now `$exception` was NOT captured at all, which made a whole
      // class of failure invisible: the 2026-07-28 activation dig found 38 of
      // 46 new churches abandoning within 60s of opening a class template
      // (median 0s), and NO query could tell a crashing editor apart from a
      // visitor who simply left — because a thrown error produced no event.
      // Rollbar only sees what we hand it via window.Rollbar.*, so an
      // unhandled render error in the admin reached neither tool.
      //
      // Console errors stay OFF: they're the noisy channel (every third-party
      // script warning) and deliberate logging already goes to Rollbar. We
      // want crashes, not chatter.
      capture_exceptions: {
        capture_console_errors: false,
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
      },
      capture_pageview: true,
      capture_performance: {
        network_timing: true,
        web_vitals: true,
      },
      loaded: () => {
        phLoaded = true;
        // Runs BEFORE the SDK sends its initial $pageview (posthog-js captures
        // it after this callback returns), so the surface stamp lands on every
        // event including that first one.
        adoptLoadedSdk();
      },
      person_profiles: 'always',
      ui_host: 'https://us.posthog.com',
    });
  } catch (err) {
    window.Rollbar?.error(
      err instanceof Error
        ? err
        : new Error(`PostHog init threw: ${String(err)}`)
    );
  }
  // If the SDK never signals `loaded` within 10s — e.g. a content/ad blocker
  // blocked the posthog-js chunk, or init crashed — the client analytics layer
  // is dark. Report once so it surfaces in Rollbar instead of being found by
  // hand. LIMIT: this does NOT catch silent server-side drops (PostHog returns
  // 200 but discards the event). That failure is invisible to the client and
  // needs a server-side event-volume heartbeat to detect.
  window.setTimeout(() => {
    if (!phLoaded) {
      window.Rollbar?.warning(
        'PostHog SDK did not initialize within 10s — client analytics may be down'
      );
    }
  }, 10000);
}

// SPA navigation: re-stamp the surface, then count the pageview. One visitor
// can cross surfaces without a full page load (login → /admin, admin →
// previewing their church's guest pages), and `register` persists — a stale
// value would mislabel everything after the crossing. Shared by both router
// trees (usePostHog below, App Router AnalyticsProvider).
export function trackSpaNavigation(url: string): void {
  if (typeof window === 'undefined') return;

  posthogJs.register({ surface: surfaceForPath(url) });
  posthogJs.capture('$pageview');
}

export function usePostHog() {
  const router = useRouter();

  // Init MUST be inside useEffect (with empty deps) — otherwise it runs on
  // every render of _app.tsx and re-initializes the SDK on every navigation
  // or prop change. posthog-js 1.55 silently ignored repeated init calls,
  // but the upgrade to 1.372 changed that behavior: each re-init resets
  // session state, so the initial pageview fires and then subsequent events
  // (form interactions, downstream pageviews, the eventual $identify) land
  // under a fresh session. Result: every session looks like a single-
  // pageview bounce, $initial_utm_* gets lost off pageview events, and the
  // daily-by-campaign attribution query goes blind. initPostHogOnce's module
  // guard plus the one-shot effect make init idempotent at both layers.
  useEffect(() => {
    initPostHogOnce();
  }, []);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      trackSpaNavigation(url);
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);
}

const posthog = typeof window !== 'undefined' ? posthogJs : undefined;

export default posthog;
